import type {UserDefinedObjectTemplateAdditionalColumn as UdoColumn} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {getUserDefinedObjectAdditionalColumnDetails} from "~/domainModels/userDefinedObjectAdditionalColumns";
import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";

import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import type {BtlActivity} from "~/domainModels/btlActivities";

import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";

import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {ProductCatalogue} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogue";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {
    AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID,
    AMARON_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    AmaronEventTypes,
    amaronCallEventSyntheticFunction,
    buildAmaronCallSyntheticColumnValues,
    formatAmaronCallStatusIdForDisplay,
    getAmaronDefaultVisibleFilterColumnIdsForEventTemplate,
    mergeUniqueOrderedColumnIds,
} from "~/domainModels/workspaceSpecific/amaron";
import {AMARON_EVENT_CALLS_DISPOSITION_LEVEL_BY_COLUMN_ID, buildAmaronDispositionFilterColumn} from "~/domainModels/workspaceSpecific/amaronDispositionFilters";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
// import {getUserDisplayName} from "~/domainModels/workspaceSpecific/common";
// import type {BtlActivity, OrganizationalUnit, OrganizationalUnitTemplate, Product, User, UserTemplate, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventWithEventContainer} from "~/utilities/apis/api";
import {formatDateTime, getUserDisplayName, type PlatformUserDisplayDetails} from "~/utilities/utilities";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";

export {amaronCallEventSyntheticFunction, buildAmaronCallSyntheticColumnValues};
const WORKSPACE_USER_REFERENCE_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatWorkspaceUserReferenceForDisplay(
    value: string,
    users: Array<User>,
    userTemplates: Array<UserTemplate>,
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>,
): string {
    const trimmed = value.trim();
    if (trimmed === "") {
        return "";
    }
    if (WORKSPACE_USER_REFERENCE_UUID_PATTERN.test(trimmed) !== true) {
        return trimmed;
    }
    const displayName = getUserDisplayName(trimmed as Uuid, users, userTemplates, platformUserDetailsByUserId);
    if (displayName === "?" || displayName === "-") {
        return "-";
    }
    if (WORKSPACE_USER_REFERENCE_UUID_PATTERN.test(displayName) === true) {
        return "-";
    }
    return displayName;
}

function buildAmaronWorkspaceUserReferenceColumn(
    id: string,
    displayName: string,
    getRawUserId: (event: EventWithEventContainer) => unknown,
    users: Array<User>,
    userTemplates: Array<UserTemplate>,
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>,
): OttopilotColumn<EventWithEventContainer> {
    return {
        id: id,
        type: OttopilotColumnType.string,
        displayName: displayName,
        valueGetter: (event: EventWithEventContainer) => {
            const raw = getRawUserId(event);
            if (raw === null || raw === undefined) {
                return "";
            }
            return String(raw).trim();
        },
        valueFormatter: (value: string) => formatWorkspaceUserReferenceForDisplay(value, users, userTemplates, platformUserDetailsByUserId),
        sortingCapability: true,
        filteringCapability_contains: true,
    };
}

// ---------------------------------------------------------------------------
// Template patcher — adds synthetic columns + corrects column types
// ---------------------------------------------------------------------------

// Full column-object shape stored in eventTemplate.additionalColumns
type EventAdditionalColumn = UdoColumn;
// Full column-object shape stored in journeysModuleConfiguration.additionalColumns
type EventContainerAdditionalColumn = UdoColumn;

export function buildAmaronCallTableColumns(params: {
    eventTemplate: OttopilotEventTemplate;
    eventTemplates: OttopilotEventTemplate[];
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    organizationalUnits: Array<OrganizationalUnit>;
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
    btlActivities: Array<BtlActivity>;
    products: Array<ProductCatalogue>;
    eventContainerTemplate: EventContainerTemplate;
    journeyAdditionalColumnById: Map<Uuid, EventContainerAdditionalColumn>;
    eventAdditionalColumnById: Map<Uuid, EventAdditionalColumn>;
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
}): Array<OttopilotColumn<EventWithEventContainer>> {
    const {eventTemplate} = params;
    if ((eventTemplate.id as string) !== String(AmaronEventTypes.Calls)) {
        return [];
    }

    return mergeUniqueOrderedColumnIds(
        AMARON_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
        getAmaronDefaultVisibleFilterColumnIdsForEventTemplate(eventTemplate.id),
    ).map((id): OttopilotColumn<EventWithEventContainer> => {
        // System columns
        if (id === "updatedBy" || id === "createdBy") {
            return {
                id: id,
                type: OttopilotColumnType.uuid,
                displayName: id === "updatedBy" ? "Updated by" : "Created by",
                valueGetter: (event) => (id === "updatedBy" ? event.updatedBy : event.createdBy),
                valueFormatter: (value) => getUserDisplayName(value, params.users, params.userTemplates, params.platformUserDetailsByUserId),
                sortingCapability: false,
                filteringCapability_contains: false,
            };
        }
        if (id === "updatedAt" || id === "createdAt") {
            return {
                id: id,
                type: OttopilotColumnType.iso8601DateTime,
                displayName: id === "updatedAt" ? "Updated at" : "Created at",
                valueGetter: (event) => (id === "updatedAt" ? event.updatedAt : event.createdAt),
                valueFormatter: (value) => formatDateTime(value),
                sortingCapability: true,
                filteringCapability_contains: false,
            };
        }

        // Synthetic Disposition Columns (L1–L4)
        if (
            id === AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID ||
            id === AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID ||
            id === AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID ||
            id === AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID
        ) {
            const dispositionLevel = AMARON_EVENT_CALLS_DISPOSITION_LEVEL_BY_COLUMN_ID.get(id);
            if (dispositionLevel === undefined) {
                throw new Error(`Unknown Amaron calls disposition column: ${id}`);
            }
            return buildAmaronDispositionFilterColumn<EventWithEventContainer>({
                scope: "eventCalls",
                columnId: id,
                dispositionLevel,
                valueGetter: (event) => String(event.additionalColumnValues[id as Uuid] ?? ""),
            });
        }

        // Journey Columns
        const journeyColumn = params.journeyAdditionalColumnById.get(id as Uuid);
        if (journeyColumn) {
            if (journeyColumn.type === UdoColumnType.user) {
                return buildAmaronWorkspaceUserReferenceColumn(
                    id,
                    journeyColumn.displayName,
                    (event) => event.eventContainer.additionalColumnValues[id as Uuid],
                    params.users,
                    params.userTemplates,
                    params.platformUserDetailsByUserId,
                );
            }
            return {
                id: id,
                type: OttopilotColumnType.string,
                displayName: journeyColumn.displayName,
                valueGetter: (event: EventWithEventContainer) =>
                    getUserDefinedObjectAdditionalColumnDetails(journeyColumn).stringValueGetter({
                        value: event.eventContainer.additionalColumnValues[id as Uuid] as never,
                        users: params.users,
                        userTemplates: params.userTemplates,
                        platformUserDetailsByUserId: params.platformUserDetailsByUserId,
                        organizationalUnits: params.organizationalUnits,
                        organizationalUnitTemplates: params.organizationalUnitTemplates,
                        products: params.products,
                    }),
                sortingCapability: true,
                filteringCapability_contains: true,
            };
        }

        // Event Columns
        const eventColumn = params.eventAdditionalColumnById.get(id as Uuid);
        if (eventColumn) {
            // Select options injected manually
            if (id === AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID) {
                return {
                    id: id,
                    type: OttopilotColumnType.string,
                    displayName: eventColumn.displayName,
                    valueGetter: (event: EventWithEventContainer) => {
                        const val = event.additionalColumnValues[id as Uuid];
                        if (val === null || val === undefined) {
                            return "";
                        }
                        return String(val);
                    },
                    valueFormatter: (value) => formatAmaronCallStatusIdForDisplay(String(value ?? "")),
                    sortingCapability: true,
                    filteringCapability_contains: true,
                };
            }
            if (eventColumn.type === UdoColumnType.timestamp) {
                return {
                    id: id,
                    type: OttopilotColumnType.iso8601DateTime,
                    displayName: eventColumn.displayName,
                    valueGetter: (event: EventWithEventContainer) => {
                        const raw = event.additionalColumnValues[id as Uuid];
                        if (raw === null || raw === undefined || String(raw).trim() === "") {
                            return "" as Iso8601DateTime;
                        }
                        return String(raw) as Iso8601DateTime;
                    },
                    valueFormatter: (value) => formatDateTime(String(value ?? "")),
                    sortingCapability: true,
                    filteringCapability_contains: true,
                };
            }

            if (eventColumn.type === UdoColumnType.user) {
                return buildAmaronWorkspaceUserReferenceColumn(
                    id,
                    eventColumn.displayName,
                    (event) => event.additionalColumnValues[id as Uuid],
                    params.users,
                    params.userTemplates,
                    params.platformUserDetailsByUserId,
                );
            }

            // Standard Event Column mapping
            return {
                id: id,
                type: OttopilotColumnType.string,
                displayName: eventColumn.displayName,
                valueGetter: (event: EventWithEventContainer) =>
                    getUserDefinedObjectAdditionalColumnDetails(eventColumn).stringValueGetter({
                        value: event.additionalColumnValues[id as Uuid] as never,
                        users: params.users,
                        userTemplates: params.userTemplates,
                        platformUserDetailsByUserId: params.platformUserDetailsByUserId,
                        organizationalUnits: params.organizationalUnits,
                        organizationalUnitTemplates: params.organizationalUnitTemplates,
                        products: params.products,
                    }),
                sortingCapability: true,
                filteringCapability_contains: true,
            };
        }

        // Fallback
        return {
            id: id,
            type: OttopilotColumnType.string,
            displayName: id,
            valueGetter: (event: EventWithEventContainer) => String(event.additionalColumnValues[id as Uuid] ?? ""),
            sortingCapability: true,
            filteringCapability_contains: true,
        };
    });
}
